import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { reportError } from '../../lib/sentry';
import { GENERAL_FONT, MHColours } from '../../constants/theme';
import FlashSnackbar, { Severity } from '../common/FlashSnackbar';
import { hasStatus, serverMessage } from '../../lib/services/httpError';
import {
  BookingResult,
  RunState,
  RunSummary,
  getVisitRun,
  listVisitRuns,
  startVisitRun,
} from '../../lib/services/visitsService';

const POLL_MS = 2000;
const MAX_BOOKING_IDS = 200;

const todayIso = () => dayjs().format('YYYY-MM-DD');

const parseBookingIds = (raw: string): string[] | undefined => {
  const ids = raw
    .split(/[\s,]+/)
    .map((id) => id.trim())
    .filter(Boolean);
  return ids.length ? ids : undefined;
};

const outcomeColour = (outcome: BookingResult['outcome']) => {
  if (outcome === 'created') return 'success' as const;
  if (outcome === 'failed') return 'error' as const;
  return 'default' as const;
};

const VisitRunManager: React.FC = () => {
  const { t } = useTranslation();

  const [date, setDate] = useState(todayIso());
  const [bookingIdsInput, setBookingIdsInput] = useState('');
  const [starting, setStarting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [run, setRun] = useState<RunState | null>(null);
  const [history, setHistory] = useState<RunSummary[]>([]);
  const [flash, setFlash] = useState<{
    open: boolean;
    msg: string;
    severity: Severity;
  }>({ open: false, msg: '', severity: 'success' });

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const detailRef = useRef<HTMLDivElement | null>(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const loadHistory = useCallback(() => {
    listVisitRuns(10)
      .then(({ data }) => setHistory(data.result.runs))
      .catch((err) => {
        reportError(err);
      });
  }, []);

  useEffect(() => {
    loadHistory();
    return stopPolling;
  }, [loadHistory]);

  const followRun = useCallback(
    (runId: string) => {
      stopPolling();
      const tick = async () => {
        try {
          const { data } = await getVisitRun(runId);
          setRun(data.result);
          if (data.result.status !== 'running') {
            stopPolling();
            loadHistory();
          }
        } catch (err) {
          stopPolling();
          reportError(err);
          setFlash({
            open: true,
            msg: t('visitRun.pollError'),
            severity: 'error',
          });
        }
      };
      void tick();
      pollRef.current = setInterval(tick, POLL_MS);
    },
    [loadHistory, t]
  );

  const showRun = (runId: string) => {
    followRun(runId);
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const startErrorMessage = (err: unknown) => {
    if (hasStatus(err, 409)) {
      return t('visitRun.alreadyRunning');
    }
    return serverMessage(err) ?? t('visitRun.startError');
  };

  const start = async (live: boolean) => {
    setStarting(true);
    setRun(null);
    try {
      const { data } = await startVisitRun({
        date,
        bookingIds: parseBookingIds(bookingIdsInput),
        ...(live ? { confirm: 'RUN' as const } : {}),
      });
      setFlash({
        open: true,
        msg: live ? t('visitRun.started') : t('visitRun.previewStarted'),
        severity: 'success',
      });
      followRun(data.result.runId);
    } catch (err) {
      const conflict = hasStatus(err, 409);
      reportError(err);
      setFlash({
        open: true,
        msg: startErrorMessage(err),
        severity: conflict ? 'warning' : 'error',
      });
    } finally {
      setStarting(false);
    }
  };

  const label = (prefix: string, code?: string) => {
    if (!code) return '';
    const key = code.startsWith('http_') ? 'http' : code;
    return t(`visitRun.${prefix}.${key}`, { defaultValue: code });
  };

  const renderPatientCell = (result: BookingResult) => {
    if (result.userId) {
      return (
        <Link component={RouterLink} to={`/dashboard/${result.userId}`}>
          {result.userId}
        </Link>
      );
    }
    if (result.userMissing) {
      return t('visitRun.userMissing');
    }
    return '—';
  };

  const renderResults = (results: BookingResult[]) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('visitRun.column.booking')}</TableCell>
          <TableCell>{t('visitRun.column.patient')}</TableCell>
          <TableCell>{t('visitRun.column.outcome')}</TableCell>
          <TableCell>{t('visitRun.column.detail')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {results.map((result) => (
          <TableRow key={result.bookingId}>
            <TableCell>{result.bookingId}</TableCell>
            <TableCell>{renderPatientCell(result)}</TableCell>
            <TableCell>
              <Chip
                size="small"
                color={outcomeColour(result.outcome)}
                label={t(`visitRun.outcome.${result.outcome}`)}
              />
            </TableCell>
            <TableCell>
              {label('reason', result.reason)}
              {result.step ? ` · ${label('step', result.step)}` : ''}
              {result.statusCode ? ` · HTTP ${result.statusCode}` : ''}
              {result.visitId
                ? ` · ${t('visitRun.visitId', { id: result.visitId })}`
                : ''}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const bookingIdCount = parseBookingIds(bookingIdsInput)?.length ?? 0;
  const tooManyBookingIds = bookingIdCount > MAX_BOOKING_IDS;
  const running = run?.status === 'running';
  const progress = run && run.total > 0 ? (run.processed / run.total) * 100 : 0;

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography
        style={{ fontSize: 20, fontWeight: 'bold', fontFamily: GENERAL_FONT }}
      >
        {t('visitRun.title')}
      </Typography>
      <Typography
        style={{
          fontFamily: GENERAL_FONT,
          fontSize: 13,
          color: MHColours.mhGrayDark,
          margin: '8px 0 16px',
        }}
      >
        {t('visitRun.intro')}
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            }}
          >
            <TextField
              size="small"
              type="date"
              label={t('visitRun.date')}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={starting || running}
              sx={{ width: 190 }}
            />
            <TextField
              size="small"
              label={t('visitRun.bookingIds')}
              placeholder={t('visitRun.bookingIdsPlaceholder')}
              value={bookingIdsInput}
              onChange={(e) => setBookingIdsInput(e.target.value)}
              error={tooManyBookingIds}
              helperText={
                tooManyBookingIds
                  ? t('visitRun.tooManyBookingIds', {
                      entered: bookingIdCount,
                      max: MAX_BOOKING_IDS,
                    })
                  : t('visitRun.bookingIdsHelp')
              }
              disabled={starting || running}
              sx={{ flexGrow: 1, minWidth: 320 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              disabled={starting || running || tooManyBookingIds}
              onClick={() => start(false)}
              sx={{ textTransform: 'none' }}
            >
              {t('visitRun.preview')}
            </Button>
            <Button
              variant="contained"
              disabled={starting || running || tooManyBookingIds}
              onClick={() => setConfirmOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: MHColours.mhGreen,
                '&:hover': { backgroundColor: MHColours.mhGreenDark },
              }}
            >
              {t('visitRun.run')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <div ref={detailRef} />

      {run && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                style={{ fontFamily: GENERAL_FONT, fontWeight: 'bold' }}
              >
                {t(`visitRun.status.${run.status}`)}
              </Typography>
              {run.dryRun && (
                <Chip size="small" color="info" label={t('visitRun.dryRun')} />
              )}
              <Typography
                style={{
                  fontFamily: GENERAL_FONT,
                  fontSize: 13,
                  color: MHColours.mhGrayDark,
                }}
              >
                {run.date} · {run.processed}/{run.total} ·{' '}
                {t('visitRun.counts', {
                  created: run.created,
                  skipped: run.skipped,
                  failed: run.failed,
                })}
              </Typography>
            </Box>

            {running && (
              <LinearProgress
                variant={run.total ? 'determinate' : 'indeterminate'}
                value={progress}
                sx={{ mt: 2 }}
              />
            )}

            {run.status === 'interrupted' && (
              <Typography
                style={{
                  fontFamily: GENERAL_FONT,
                  fontSize: 13,
                  marginTop: 12,
                }}
              >
                {t('visitRun.interruptedHelp')}
              </Typography>
            )}

            <Box sx={{ mt: 2 }}>{renderResults(run.results)}</Box>
          </CardContent>
        </Card>
      )}

      <Typography
        style={{
          fontFamily: GENERAL_FONT,
          fontWeight: 'bold',
          marginBottom: 8,
        }}
      >
        {t('visitRun.history')}
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('visitRun.column.started')}</TableCell>
                <TableCell>{t('visitRun.column.date')}</TableCell>
                <TableCell>{t('visitRun.column.trigger')}</TableCell>
                <TableCell>{t('visitRun.column.status')}</TableCell>
                <TableCell>{t('visitRun.column.counts')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((item) => (
                <TableRow key={item.runId}>
                  <TableCell>
                    {new Date(item.startedAt).toLocaleString('sv-SE')}
                  </TableCell>
                  <TableCell>{item.date}</TableCell>
                  <TableCell>{t(`visitRun.trigger.${item.trigger}`)}</TableCell>
                  <TableCell>
                    {t(`visitRun.status.${item.status}`)}
                    {item.dryRun ? ` (${t('visitRun.dryRun')})` : ''}
                  </TableCell>
                  <TableCell>
                    {t('visitRun.counts', {
                      created: item.created,
                      skipped: item.skipped,
                      failed: item.failed,
                    })}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => showRun(item.runId)}
                      sx={{ textTransform: 'none' }}
                    >
                      {t('visitRun.show')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!history.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography
                      style={{ fontFamily: GENERAL_FONT, fontSize: 13 }}
                    >
                      {t('visitRun.noHistory')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{String(t('visitRun.confirmLiveTitle'))}</DialogTitle>
        <DialogContent>{String(t('visitRun.confirmLive'))}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>
            {String(t('cancel'))}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={starting}
            onClick={() => {
              setConfirmOpen(false);
              void start(true);
            }}
          >
            {String(t('visitRun.run'))}
          </Button>
        </DialogActions>
      </Dialog>

      <FlashSnackbar
        open={flash.open}
        msg={flash.msg}
        severity={flash.severity}
        onClose={() => setFlash((f) => ({ ...f, open: false }))}
      />
    </Box>
  );
};

export default VisitRunManager;
